/**
 * unreachableReasons.ts — HOW contact failed, as the picker reads it.
 *
 * Owner repo: @thegpservice/gpsclinics-portal. Server half:
 * clinic-service `src/deals/unreachable-reason.ts`.
 *
 * ⚠️ THIS IS A MIRROR, AND THE SERVER IS THE CONTROL (R41) — the same posture
 * as `closedReasons.ts` beside it. Every rule here is enforced again in
 * clinic-service, which refuses a code from the wrong vocabulary, refuses
 * `other` with no note, and refuses the move itself under three call attempts
 * from an admin round. Where the two disagree the server wins and its own
 * sentence is what the operator reads.
 *
 * ⚠️ THE KEYS MUST MATCH THE SERVER'S EXACTLY. They are what is written and
 * grouped in every report, so a typo does not fail loudly — it posts a value
 * the server's enum rejects and hands the staff member a 400 they cannot act
 * on. Never re-spell a key on either side; append in both.
 *
 * ⚠️ AND IT IS A DIFFERENT LIST FROM `closedReasons.ts` ON PURPOSE. That one
 * answers *why did we lose them*; this one answers *how did contact fail*, on
 * a card that is NOT closed and may well come back. Offering "Too expensive"
 * on a move whose whole premise is that nobody has spoken to the patient would
 * invite a staff member to record something they cannot know.
 */

/** The order they appear in the picker — commonest first, `Other` last. */
export const UNREACHABLE_REASON_OPTIONS = [
  { code: 'no_answer', label: 'Rings out — no answer' },
  { code: 'voicemail_no_response', label: 'Left a voicemail, no reply' },
  { code: 'number_unobtainable', label: 'Number not in service' },
  { code: 'wrong_number', label: 'Wrong number — someone else answered' },
  { code: 'other', label: 'Other' },
] as const;

export type UnreachableReasonCode = (typeof UNREACHABLE_REASON_OPTIONS)[number]['code'];

/** Reasons that say nothing on their own, so the note is required beside them. */
const NEEDS_DETAIL: readonly UnreachableReasonCode[] = ['other'];

export function unreachableReasonNeedsDetail(code: UnreachableReasonCode | ''): boolean {
  return code !== '' && NEEDS_DETAIL.includes(code);
}

/**
 * The lanes where the server applies the three-attempt gate to this move — the
 * two ADMIN call rounds. `plan_sent` is absent because no round runs there,
 * which is exactly why the 68 stuck cards move at zero attempts.
 *
 * ⚠️ AFFORDANCE ONLY. This decides whether the dialog WARNS before submit; the
 * refusal itself is the server's 409, surfaced verbatim. Do not turn this into
 * a hard block — a card whose count this screen cannot see must still be
 * submittable, and the server is the one that knows.
 */
const GATED_STAGES: readonly string[] = ['awaiting_admin_followup', 'pending_admin_review'];

export const REQUIRED_ROUND_ATTEMPTS = 3;

/**
 * The warning to show the moment the form opens on a gated lane, or null.
 *
 * Shown UP FRONT rather than on submit: it is the one refusal this screen can
 * see coming, and naming it while the picker is still open makes it advice
 * rather than a rejection. `null` attempts means we genuinely do not know —
 * say nothing rather than guess, and let the server answer.
 */
export function unreachableAttemptsWarning(
  stage: string,
  attemptsThisRound: number | null,
): string | null {
  if (!GATED_STAGES.includes(stage)) return null;
  if (attemptsThisRound === null || attemptsThisRound >= REQUIRED_ROUND_ATTEMPTS) return null;
  const left = REQUIRED_ROUND_ATTEMPTS - attemptsThisRound;
  return `This deal is in an active admin call round with ${attemptsThisRound} of ${REQUIRED_ROUND_ATTEMPTS} attempts logged. Log ${left} more, or — if you did reach them — hand the deal over or close it with the reason they gave.`;
}
